import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BackLink } from 'ui/BackLink';
import { isAdmin, isFinanceUser, isSr } from 'store/modules/auth/selectors';
import FluidButton from 'ui/FluidButton';
import { LeaveWithoutSavingModal } from 'ui/LeaveWithoutSavingModal';
import { useNotifications } from 'hooks/useNotifications';
import { useModal } from 'hooks/useModal';
import { TCurrencyCode } from 'interfaces';
import { ESortOrder } from 'store/common/types';
import {
  IBankTransaction,
  IBankTransactionPatchRequest,
  IBookingAllocationState,
  IDepositAccountAllocationState,
  IAllocatableBooking,
  IAllocation,
  IAmountAllocation,
  TAllocatableBookingSortKey,
  EBankTransactionProcessingStatus,
  isDebit,
  IBankTransactionDetailLocationState,
} from './types';
import { formatPriceCents, formatDateDisplay } from 'utils';
import { getCurrencySymbol } from 'utils/currency';
import { TextInput } from 'ui/TextInput';
import { LoadingBar } from 'ui/NetworkStatusBar';
import ConfirmationModal, { EConfirmationModalType } from 'ui/ConfirmationModal';
import { useBankTransaction } from './hooks/useBankTransactionQueries';
import { useAllocatableBookingsQuery } from './hooks/useAllocatableBookingsQuery';
import { useExchangeRateQuery } from './hooks/useExchangeRateQuery';
import { useSaveDepositAccount } from './hooks/useSaveDepositAccount';
import { useProcessBankTransaction } from './hooks/useProcessBankTransaction';
import { DepositAccountSection } from './components/DepositAccountSection';
import { AuditInfo } from './components/AuditInfo';
import { AllocateToBookingsSection } from './components/AllocateToBookingsSection';
import { AllocateToDepositAccountSection } from './components/AllocateToDepositAccountSection';
import { AllocationSummary } from './components/AllocationSummary';
import { calculateTotalBookingsAllocatedCents, calculateBalanceToAllocateCents, formatDateShort } from './utils';
import { BankTransactionCommentsSection } from './components/BankTransactionCommentsSection';
import { useCompaniesQuery } from './hooks/useCompaniesQuery';

interface ReceiptPageParams {
  uuid: string;
}

interface ISelectedCompany {
  uuid: string;
  name: string;
}

/**
 * Get the allocations to use based on processing status.
 * When PROCESSED, use processingHistory[versionIndex].allocations (the snapshot at time of processing).
 * Otherwise, use the current allocations.
 */
const getAllocationsForReceipt = (receipt: IBankTransaction, versionIndex: number = 0): IAllocation[] => {
  const isProcessed = receipt.processingStatus === EBankTransactionProcessingStatus.PROCESSED;
  if (isProcessed && receipt.processingHistory?.length > 0) {
    return receipt.processingHistory[versionIndex]?.allocations || [];
  }
  return receipt.allocations || [];
};

/**
 * Extract booking UUIDs from allocations.
 * Used to ensure previously allocated bookings are always fetched,
 * even if their outstanding balance is now 0.
 */
const getBookingUuidsFromAllocations = (allocations: IAllocation[]): string[] => {
  return allocations.filter(a => a.entity.type === 'BOOKING').map(a => a.entity.uuid);
};

/**
 * Initialize booking allocations from saved receipt data
 */
const initializeBookingAllocationsFromReceipt = (
  receipt: IBankTransaction,
  allocatableBookings: IAllocatableBooking[],
  versionIndex: number = 0
): IBookingAllocationState[] => {
  // Get allocations based on processing status and selected version
  const allocations = getAllocationsForReceipt(receipt, versionIndex);
  // Filter to only BOOKING type allocations
  const savedBookingAllocations = allocations.filter(a => a.entity.type === 'BOOKING');

  // Create allocation state for all allocatable bookings
  return allocatableBookings.map(booking => {
    const savedAllocation = savedBookingAllocations.find(a => a.entity.uuid === booking.bookingUuid);

    if (savedAllocation) {
      return {
        bookingUuid: booking.bookingUuid,
        humanReadableId: booking.humanReadableId,
        isSelected: true,
        description: savedAllocation.description || '',
        toAllocateCents: savedAllocation.amountCents,
      };
    }

    return {
      bookingUuid: booking.bookingUuid,
      humanReadableId: booking.humanReadableId,
      isSelected: false,
      description: '',
      toAllocateCents: booking.outstandingBalanceCents,
    };
  });
};

/**
 * Initialize deposit account allocation from saved receipt data
 */
const initializeDepositAllocationFromReceipt = (
  receipt: IBankTransaction,
  versionIndex: number = 0
): IDepositAccountAllocationState => {
  // Get allocations based on processing status and selected version
  const allocations = getAllocationsForReceipt(receipt, versionIndex);
  const savedDepositAllocation = allocations.find(a => a.entity.type === 'TC');

  if (savedDepositAllocation) {
    return {
      description: savedDepositAllocation.description || '',
      amountCents: savedDepositAllocation.amountCents,
    };
  }

  return {
    description: '',
    amountCents: 0,
  };
};

export const ReceiptPage: React.FC = () => {
  const { uuid } = useParams<ReceiptPageParams>();
  const history = useHistory();
  const location = useLocation<IBankTransactionDetailLocationState>();
  const userIsAdmin = useSelector(isAdmin);
  const userIsFinance = useSelector(isFinanceUser);
  const userIsSr = useSelector(isSr);
  const { showSuccessNotification, showErrorNotification, showWarningNotification } = useNotifications();

  // Get return URL from location state, or fall back to base URL
  const backUrl = location.state?.returnUrl || '/finance/bank-transactions';

  const canViewReceipt = userIsAdmin || userIsFinance || userIsSr;

  useEffect(() => {
    if (!canViewReceipt) {
      history.push('/');
    }
  }, [canViewReceipt, history]);

  // Fetch receipt data
  const { data: response, isLoading, isError, refetch } = useBankTransaction(uuid, canViewReceipt);

  const receipt: IBankTransaction | undefined = response?.data;

  // Pre-fetch companies so they're cached before DepositAccountSection mounts (OWA-10571)
  const { isLoading: isLoadingCompanies } = useCompaniesQuery(canViewReceipt);

  // ============================================
  // STATE MANAGEMENT
  // ============================================

  // Deposit Account state (lifted from DepositAccountSection)
  const [selectedCompanyUuid, setSelectedCompanyUuid] = useState<string | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<ISelectedCompany | null>(null);
  const [exchangeCurrency, setExchangeCurrency] = useState<TCurrencyCode | null>(null);
  const [conversionRate, setConversionRate] = useState<string>('1');

  // Allocation state
  const [bookingAllocations, setBookingAllocations] = useState<IBookingAllocationState[]>([]);
  const [depositAllocation, setDepositAllocation] = useState<IDepositAccountAllocationState>({
    description: '',
    amountCents: 0,
  });

  // Sorting state for bookings table
  const [sortBy, setSortBy] = useState<TAllocatableBookingSortKey>('arrivalDate');
  const [sortOrder, setSortOrder] = useState<ESortOrder>(ESortOrder.ASC);

  // Filter state for bookings table
  const [showOnlySelected, setShowOnlySelected] = useState(false);

  // Dirty state tracking
  const [isDirty, setIsDirty] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const userChangedCurrency = useRef(false);

  // Processing history version selector (for processed transactions)
  const [selectedVersionIndex, setSelectedVersionIndex] = useState<number>(0);

  // ============================================
  // QUERIES
  // ============================================

  // Fetch exchange rate when currency changes
  const { data: fetchedExchangeRate, isFetching: isFetchingExchangeRate } = useExchangeRateQuery(
    receipt?.currency || null,
    exchangeCurrency,
    !!receipt && !!exchangeCurrency
  );

  // Extract booking UUIDs from saved allocations to ensure they're always fetched
  // (even if their outstanding balance is now 0).
  // Only include saved UUIDs when the current currency matches the saved currency -
  // otherwise the UUIDs belong to a different currency and the API will reject them (OWA-10501).
  const savedCurrency = receipt?.exchangeCurrency ?? receipt?.currency;
  const includeBookingUuids = receipt && exchangeCurrency === savedCurrency
    ? getBookingUuidsFromAllocations(getAllocationsForReceipt(receipt, selectedVersionIndex))
    : [];

  // Fetch allocatable bookings when TC and currency are selected
  const {
    data: allocatableBookingsData,
    isLoading: isLoadingBookings,
    isFetching: isFetchingBookings,
  } = useAllocatableBookingsQuery(
    selectedCompanyUuid,
    exchangeCurrency,
    { key: sortBy, order: sortOrder === ESortOrder.ASC ? 'asc' : 'desc' },
    includeBookingUuids,
    !!selectedCompanyUuid && !!exchangeCurrency
  );

  const allocatableBookings = allocatableBookingsData?.bookings || [];

  // Save mutation
  const saveDepositMutation = useSaveDepositAccount();

  // Process mutation
  const processBankTransactionMutation = useProcessBankTransaction();

  // Modal for confirm and process
  const processModal = useModal();

  // ============================================
  // INITIALIZATION FROM RECEIPT
  // ============================================

  // Initialize state from receipt on load.
  // Also waits for companies to be loaded so that DepositAccountSection's Multiselect
  // mounts with valid options, preventing auto-correction race conditions (OWA-10571).
  useEffect(() => {
    if (receipt && !isInitialized && !isLoadingCompanies) {
      setSelectedCompanyUuid(receipt.counterparty?.uuid ?? null);
      setSelectedCompany(
        receipt.counterparty ? { uuid: receipt.counterparty.uuid, name: receipt.counterparty.name } : null
      );
      setExchangeCurrency(receipt.exchangeCurrency ?? receipt.currency);
      setConversionRate(receipt.exchangeRate?.toString() ?? '1');
      setDepositAllocation(initializeDepositAllocationFromReceipt(receipt, selectedVersionIndex));
      setIsInitialized(true);
    }
  }, [receipt, isInitialized, isLoadingCompanies]);

  // Initialize booking allocations when allocatable bookings are fetched
  useEffect(() => {
    if (receipt && allocatableBookings.length > 0 && isInitialized) {
      setBookingAllocations(
        initializeBookingAllocationsFromReceipt(receipt, allocatableBookings, selectedVersionIndex)
      );
    }
  }, [receipt, allocatableBookings, isInitialized, selectedVersionIndex]);

  // Re-initialize allocations when selected version changes (for processed transactions)
  useEffect(() => {
    if (receipt && isInitialized && isProcessed) {
      setDepositAllocation(initializeDepositAllocationFromReceipt(receipt, selectedVersionIndex));
      if (allocatableBookings.length > 0) {
        setBookingAllocations(
          initializeBookingAllocationsFromReceipt(receipt, allocatableBookings, selectedVersionIndex)
        );
      }
    }
  }, [selectedVersionIndex]);

  // Update conversion rate when exchange rate is fetched (only if user explicitly changed currency)
  useEffect(() => {
    if (fetchedExchangeRate !== undefined && !isFetchingExchangeRate && isInitialized) {
      // Only update rate if user explicitly changed currency (not on initial load)
      // This preserves the saved exchangeRate from the receipt
      if (userChangedCurrency.current) {
        setConversionRate(fetchedExchangeRate.toString());
        setIsDirty(true);
        userChangedCurrency.current = false;
      }
    }
  }, [fetchedExchangeRate, isFetchingExchangeRate, isInitialized]);

  // ============================================
  // COMPUTED VALUES
  // ============================================

  // Calculate converted amount in cents
  // Rate format is "1 source = X target", so multiply to convert source → target
  const convertedAmountCents = useMemo(() => {
    if (!receipt) return 0;
    const rate = parseFloat(conversionRate);
    if (!rate || rate <= 0) return 0;
    return receipt.amountCents * rate;
  }, [receipt?.amountCents, conversionRate]);

  // Check if the receipt is already processed (read-only mode)
  const isProcessed = receipt?.processingStatus === EBankTransactionProcessingStatus.PROCESSED;

  // Generate version options for the processing history dropdown
  const versionOptions = useMemo(() => {
    if (!receipt?.processingHistory?.length) return [];

    // Sort by processedAt descending (most recent first in dropdown)
    const sorted = [...receipt.processingHistory]
      .map((item, originalIndex) => ({ item, originalIndex }))
      .sort((a, b) => new Date(b.item.processedAt).getTime() - new Date(a.item.processedAt).getTime());

    // Generate version labels (most recent = highest version number)
    return sorted.map((entry, sortedIdx) => {
      const versionNumber = sorted.length - sortedIdx; // e.g., V3, V2, V1
      const date = formatDateShort(entry.item.processedAt);
      const email = entry.item.processedBy.email;
      return {
        value: entry.originalIndex.toString(),
        label: `V${versionNumber}, ${date} / ${email}`,
      };
    });
  }, [receipt?.processingHistory]);

  // Check if allocation section is enabled (TC + currency selected)
  const isAllocationEnabled = !!selectedCompanyUuid && !!exchangeCurrency;

  // Calculate total allocated to bookings (for balance and modal)
  const totalBookingsAllocatedCents = useMemo(() => {
    return calculateTotalBookingsAllocatedCents(bookingAllocations);
  }, [bookingAllocations]);

  // Calculate balance to allocate
  const balanceToAllocateCents = useMemo(() => {
    return calculateBalanceToAllocateCents(
      convertedAmountCents,
      totalBookingsAllocatedCents,
      depositAllocation.amountCents
    );
  }, [convertedAmountCents, totalBookingsAllocatedCents, depositAllocation.amountCents]);

  // Check if Confirm and Process button should be enabled
  // Enabled when: balance === 0 AND status !== PROCESSED
  const canProcess = useMemo(() => {
    if (!receipt) return false;
    // Use 1 cent tolerance for floating point precision (matches what displays as € 0.00)
    const isBalanceZero = balanceToAllocateCents === 0;
    const isNotProcessed = receipt.processingStatus !== EBankTransactionProcessingStatus.PROCESSED;
    return isBalanceZero && isNotProcessed;
  }, [receipt, balanceToAllocateCents]);

  // Check if user can see the Confirm and Process button
  const canSeeProcessButton = (userIsAdmin || userIsFinance) && !userIsSr;

  // Currency symbol for modal messages
  const currencySymbol = getCurrencySymbol((exchangeCurrency || receipt?.currency) as any);

  // Processing state for button loading
  const isProcessing = saveDepositMutation.isPending || processBankTransactionMutation.isPending;

  // ============================================
  // HANDLERS
  // ============================================

  // Handle TC change - clear all allocations when TC changes
  const handleCompanyChange = useCallback((companyUuid: string | null, company: ISelectedCompany | null) => {
    setSelectedCompanyUuid(companyUuid);
    setSelectedCompany(company);
    // Clear allocations when TC changes
    setBookingAllocations([]);
    setDepositAllocation({ description: '', amountCents: 0 });
    setIsDirty(true);
  }, []);

  // Handle currency change - clear all allocations when currency changes
  const handleCurrencyChange = useCallback(
    (currency: TCurrencyCode) => {
      // Early return if not initialized yet or currency hasn't actually changed
      // (prevents spurious dirty state from Multiselect auto-correction for non-USD/EUR currencies)
      if (!isInitialized || currency === exchangeCurrency) {
        return;
      }
      userChangedCurrency.current = true;
      setExchangeCurrency(currency);
      // If same as BT currency, set rate to 1 immediately
      if (receipt && currency === receipt.currency) {
        setConversionRate('1');
      }
      // Clear allocations when currency changes
      setBookingAllocations([]);
      setDepositAllocation({ description: '', amountCents: 0 });
      setIsDirty(true);
    },
    [receipt, exchangeCurrency, isInitialized]
  );

  // Handle conversion rate change
  const handleConversionRateChange = useCallback((rate: string) => {
    setConversionRate(rate);
    setIsDirty(true);
  }, []);

  // Handle booking sort change
  const handleBookingSort = useCallback((newSortBy: TAllocatableBookingSortKey, newSortOrder: ESortOrder) => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  }, []);

  // Handle booking selection toggle
  const handleToggleBooking = useCallback((bookingUuid: string, booking: IAllocatableBooking) => {
    setBookingAllocations(prev => {
      const existing = prev.find(a => a.bookingUuid === bookingUuid);
      if (existing) {
        // Toggle selection - if unselecting, reset description and toAllocate to original outstanding balance
        const newIsSelected = !existing.isSelected;
        return prev.map(a =>
          a.bookingUuid === bookingUuid
            ? {
                ...a,
                isSelected: newIsSelected,
                // Reset to defaults when unselecting
                description: newIsSelected ? a.description : '',
                toAllocateCents: newIsSelected ? a.toAllocateCents : booking.outstandingBalanceCents,
              }
            : a
        );
      } else {
        // Add new allocation (selected by default with outstanding balance)
        return [
          ...prev,
          {
            bookingUuid,
            humanReadableId: booking.humanReadableId,
            isSelected: true,
            description: '',
            toAllocateCents: booking.outstandingBalanceCents,
          },
        ];
      }
    });
    setIsDirty(true);
  }, []);

  // Handle select all bookings
  const handleSelectAllBookings = useCallback(() => {
    const selectedCount = bookingAllocations.filter(a => a.isSelected).length;
    const shouldSelectAll = selectedCount < allocatableBookings.length;

    setBookingAllocations(
      allocatableBookings.map(booking => {
        const existing = bookingAllocations.find(a => a.bookingUuid === booking.bookingUuid);
        return {
          bookingUuid: booking.bookingUuid,
          humanReadableId: booking.humanReadableId,
          isSelected: shouldSelectAll,
          // Only preserve values when selecting; reset to defaults when deselecting
          description: shouldSelectAll ? existing?.description ?? '' : '',
          toAllocateCents: shouldSelectAll
            ? existing?.toAllocateCents ?? booking.outstandingBalanceCents
            : booking.outstandingBalanceCents,
        };
      })
    );
    setIsDirty(true);
  }, [allocatableBookings, bookingAllocations]);

  // Handle booking description update
  const handleUpdateBookingDescription = useCallback((bookingUuid: string, description: string) => {
    setBookingAllocations(prev => prev.map(a => (a.bookingUuid === bookingUuid ? { ...a, description } : a)));
    setIsDirty(true);
  }, []);

  // Handle booking amount update
  const handleUpdateBookingAmount = useCallback((bookingUuid: string, amountCents: number) => {
    setBookingAllocations(prev =>
      prev.map(a => (a.bookingUuid === bookingUuid ? { ...a, toAllocateCents: amountCents } : a))
    );
    setIsDirty(true);
  }, []);

  // Handle deposit description update
  const handleUpdateDepositDescription = useCallback((description: string) => {
    setDepositAllocation(prev => ({ ...prev, description }));
    setIsDirty(true);
  }, []);

  // Handle deposit amount update
  const handleUpdateDepositAmount = useCallback((amountCents: number) => {
    setDepositAllocation(prev => ({ ...prev, amountCents }));
    setIsDirty(true);
  }, []);

  // ============================================
  // SAVE HANDLER
  // ============================================

  const handleSave = useCallback(async () => {
    if (!receipt || !selectedCompanyUuid || !selectedCompany) {
      showErrorNotification('Please select a Travel Company');
      return;
    }

    const rate = parseFloat(conversionRate);
    if (isNaN(rate) || conversionRate.trim() === '') {
      showErrorNotification('Please enter a valid conversion rate');
      return;
    }

    // Check for over-allocation before making API call
    if (balanceToAllocateCents < 0) {
      showErrorNotification('Cannot over-allocate');
      return;
    }

    // Build allocations array
    const allocations: IAmountAllocation[] = [];

    // Add selected booking allocations
    bookingAllocations
      .filter(a => a.isSelected)
      .forEach(a => {
        allocations.push({
          entity: { type: 'BOOKING', uuid: a.bookingUuid },
          amountCents: a.toAllocateCents,
          description: a.description || null,
        });
      });

    // Add deposit allocation if amount > 0
    if (depositAllocation.amountCents !== 0) {
      allocations.push({
        entity: { type: 'TC', uuid: selectedCompany.uuid },
        amountCents: depositAllocation.amountCents,
        description: depositAllocation.description || null,
      });
    }

    // Build payload
    const isSameCurrency = exchangeCurrency === receipt.currency;
    const payload: IBankTransactionPatchRequest = {
      counterparty: {
        uuid: selectedCompany.uuid,
        name: selectedCompany.name,
        type: 'TC',
      },
      exchangeCurrency: isSameCurrency ? null : exchangeCurrency!,
      exchangeRate: isSameCurrency ? null : rate,
      allocations,
    };

    try {
      await saveDepositMutation.mutateAsync({
        transactionUuid: receipt.uuid,
        payload,
      });
      showSuccessNotification('Receipt saved successfully.');
      setIsDirty(false);
    } catch (error) {
      const axiosError = error as { response?: { data?: { errors?: Array<{ message: string }> } } };
      const errorMessage = axiosError.response?.data?.errors?.[0]?.message || 'There was an error saving receipt.';
      showErrorNotification(errorMessage);
    }
  }, [
    receipt,
    selectedCompanyUuid,
    selectedCompany,
    conversionRate,
    exchangeCurrency,
    bookingAllocations,
    depositAllocation,
    balanceToAllocateCents,
    saveDepositMutation,
    showSuccessNotification,
    showErrorNotification,
  ]);

  // ============================================
  // CONFIRM AND PROCESS HANDLER
  // ============================================

  const handleConfirmAndProcess = useCallback(async () => {
    if (!receipt || !selectedCompanyUuid || !selectedCompany) {
      return;
    }

    // Show confirmation modal
    processModal.showModal();
  }, [receipt, selectedCompanyUuid, selectedCompany, processModal]);

  const handleProcessConfirm = useCallback(async () => {
    if (!receipt || !selectedCompanyUuid || !selectedCompany) {
      processModal.handleClose();
      return;
    }

    // Check for over-allocation before making API call (defense in depth)
    if (balanceToAllocateCents < 0) {
      showErrorNotification('Cannot over-allocate');
      processModal.handleClose();
      return;
    }

    const rate = parseFloat(conversionRate);

    // Build allocations array (same as handleSave)
    const allocations: IAmountAllocation[] = [];

    // Add selected booking allocations
    bookingAllocations
      .filter(a => a.isSelected)
      .forEach(a => {
        allocations.push({
          entity: { type: 'BOOKING', uuid: a.bookingUuid },
          amountCents: a.toAllocateCents,
          description: a.description || null,
        });
      });

    // Add deposit allocation if amount > 0
    if (depositAllocation.amountCents !== 0) {
      allocations.push({
        entity: { type: 'TC', uuid: selectedCompany.uuid },
        amountCents: depositAllocation.amountCents,
        description: depositAllocation.description || null,
      });
    }

    // Build payload
    const isSameCurrency = exchangeCurrency === receipt.currency;
    const payload: IBankTransactionPatchRequest = {
      counterparty: {
        uuid: selectedCompany.uuid,
        name: selectedCompany.name,
        type: 'TC',
      },
      exchangeCurrency: isSameCurrency ? null : exchangeCurrency!,
      exchangeRate: isSameCurrency ? null : rate,
      allocations,
    };

    // 1. Save first
    try {
      await saveDepositMutation.mutateAsync({
        transactionUuid: receipt.uuid,
        payload,
      });
    } catch (error) {
      const axiosError = error as { response?: { data?: { errors?: Array<{ message: string }> } } };
      const errorMessage =
        axiosError.response?.data?.errors?.[0]?.message || 'There was an error saving and processing.';
      showErrorNotification(errorMessage);
      processModal.handleClose();
      return;
    }

    // 2. Then process
    try {
      await processBankTransactionMutation.mutateAsync(receipt.uuid);
      showSuccessNotification('Receipt processed successfully.');
      setIsDirty(false);
      processModal.handleClose();
      // Re-fetch to get the updated bank transaction with processing history
      refetch();
    } catch (error) {
      const axiosError = error as { response?: { data?: { errors?: Array<{ message: string }> } } };
      const errorMessage =
        axiosError.response?.data?.errors?.[0]?.message || 'Save succeeded but process failed, please try again.';
      showWarningNotification(errorMessage);
      processModal.handleClose();
    }
  }, [
    receipt,
    selectedCompanyUuid,
    selectedCompany,
    conversionRate,
    exchangeCurrency,
    bookingAllocations,
    depositAllocation,
    balanceToAllocateCents,
    saveDepositMutation,
    processBankTransactionMutation,
    showSuccessNotification,
    showErrorNotification,
    showWarningNotification,
    processModal,
    refetch,
  ]);

  // ============================================
  // BEFOREUNLOAD HANDLER
  // ============================================

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // ============================================
  // RENDER
  // ============================================

  if (!canViewReceipt) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="receipt-page p-20px">
        <BackLink type="internal" href={backUrl}>
          Back
        </BackLink>
        <div className="mt-20px text-center">
          <LoadingBar label="Loading receipt..." />
        </div>
      </div>
    );
  }

  if (isError || !receipt) {
    return (
      <div className="receipt-page mx-[20px] md:mx-[80px]">
        <BackLink type="internal" href={backUrl}>
          Back
        </BackLink>
        <div className="mt-20px text-center">
          <span className="font-pt-sans text-15px text-gray-100">Receipt not found</span>
        </div>
      </div>
    );
  }

  // Validate that this is a credit transaction
  if (isDebit(receipt.amountCents)) {
    // Redirect debit transactions to payment page
    history.replace(`/finance/bank-transactions/payment/${uuid}`);
    return null;
  }

  return (
    <div className="receipt-page mx-[20px] md:mx-[80px]">
      {/* Leave Without Saving Modal */}
      <LeaveWithoutSavingModal when={isDirty} />

      {/* Header with Back and Title */}
      <div className="">
        <BackLink type="internal" href={backUrl}>
          Back
        </BackLink>
      </div>

      {/* Title */}
      <div className="flex items-center justify-between mb-20px">
        <span className="m-0 font-noe-display text-[36px] leading-tight font-normal text-black">
          Receipt
          <span className="text-[26px]">
            - <span className="uppercase">{receipt.currency}</span> {formatPriceCents(receipt.amountCents)}
          </span>
        </span>

        <AuditInfo
          createdAt={receipt.createdAt}
          createdBy={receipt.createdBy}
          updatedAt={receipt.updatedAt}
          updatedBy={receipt.updatedBy}
        />
      </div>

      {/* Main Info Section */}
      <div className="font-pt-sans">
        <h2 className="font-bold text-[17px] text-black">Main Info</h2>

        <div className="flex gap-15px">
          <div className="flex-1">
            <label className="text-black font-pt-sans text-13px leading-17px tracking-2xs">Transaction Date</label>
            <TextInput id="transaction-date" value={formatDateDisplay(receipt.transactionDate)} disabled />
          </div>
          <div className="flex-1">
            <label className="text-black font-pt-sans text-13px leading-17px tracking-2xs">Value Date</label>
            <TextInput id="value-date" value={formatDateDisplay(receipt.valueDate)} disabled />
          </div>
          <div className="w-1/4">
            <label className="text-black font-pt-sans text-13px leading-17px tracking-2xs">Bank</label>
            <TextInput id="bank" value={receipt.paymentMethodGroup.name} disabled />
          </div>
          <div className="w-[50px]">
            <label className="text-black font-pt-sans text-13px leading-17px tracking-2xs">Currency</label>
            <TextInput id="currency" value={receipt.currency} disabled />
          </div>
          <div className="flex-1">
            <label className="text-black font-pt-sans text-13px leading-17px tracking-2xs">Amount</label>
            <TextInput id="amount" value={formatPriceCents(receipt.amountCents)} disabled />
          </div>
          <div className="w-1/5">
            <label className="text-black font-pt-sans text-13px leading-17px tracking-2xs">Reference</label>
            <TextInput id="reference" value={receipt.reference || ''} disabled />
          </div>
        </div>
        {/* Narration - Full Width */}
        <div className="pt-20px font-pt-sans">
          <div className="pb-10px text-[14px] text-black">Narration</div>
          <div className="text-[15px] whitespace-pre-wrap">{receipt.narration}</div>
        </div>
      </div>

      {/* Deposit to Travel Company Account Section + Allocations + Actions
           Deferred until state is initialized from receipt data to prevent
           Multiselect auto-correction race condition (OWA-10571) */}
      {isInitialized && (
        <>
          <DepositAccountSection
            selectedCompanyUuid={selectedCompanyUuid}
            exchangeCurrency={exchangeCurrency || receipt.currency}
            conversionRate={conversionRate}
            convertedAmountCents={convertedAmountCents}
            isFetchingExchangeRate={isFetchingExchangeRate}
            onCompanyChange={handleCompanyChange}
            onCurrencyChange={handleCurrencyChange}
            onConversionRateChange={handleConversionRateChange}
            btCurrency={receipt.currency}
            isReadOnly={isProcessed}
            versionOptions={versionOptions}
            selectedVersionIndex={selectedVersionIndex}
            onVersionChange={setSelectedVersionIndex}
          />

          {/* Allocation Sections Container */}
          <div className="mt-30px border border-gray-20 rounded bg-white p-15px flex flex-col gap-30px">
            {/* Allocate to Bookings Section */}
            <AllocateToBookingsSection
              bookings={allocatableBookings}
              allocations={bookingAllocations}
              isLoading={isLoadingBookings || isFetchingBookings}
              isEnabled={isAllocationEnabled}
              isReadOnly={isProcessed}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleBookingSort}
              onToggleBooking={handleToggleBooking}
              onUpdateDescription={handleUpdateBookingDescription}
              onUpdateAmount={handleUpdateBookingAmount}
              onSelectAll={handleSelectAllBookings}
              exchangeCurrency={exchangeCurrency || receipt.currency}
              showOnlySelected={showOnlySelected}
              onShowOnlySelectedChange={setShowOnlySelected}
            />

            {/* Allocate to Deposit Account Section */}
            <AllocateToDepositAccountSection
              allocation={depositAllocation}
              onUpdateDescription={handleUpdateDepositDescription}
              onUpdateAmount={handleUpdateDepositAmount}
              isEnabled={isAllocationEnabled}
              isReadOnly={isProcessed}
              exchangeCurrency={exchangeCurrency || receipt.currency}
            />
          </div>

          {/* Allocation Summary */}
          <AllocationSummary
            totalReceiptCents={convertedAmountCents}
            bookingAllocations={bookingAllocations}
            depositAllocation={depositAllocation}
            exchangeCurrency={exchangeCurrency || receipt.currency}
          />

          {/* Action Buttons Row - Hidden when processed */}
          {!isProcessed && (
            <div className="mt-30px flex justify-between items-center">
              {/* Save Button - Left */}
              <FluidButton type="primary" onClick={handleSave} isLoading={saveDepositMutation.isPending}>
                Save
              </FluidButton>

              {/* Confirm and Process - Right, Admin/Finance only */}
              {canSeeProcessButton && (
                <FluidButton
                  type="primary"
                  onClick={handleConfirmAndProcess}
                  disabled={!canProcess}
                  isLoading={isProcessing}
                >
                  Confirm and Process
                </FluidButton>
              )}
            </div>
          )}
        </>
      )}
      <BankTransactionCommentsSection transactionUuid={uuid} showSuccessNotification={showSuccessNotification} showErrorNotification={showErrorNotification}/>
      {/* Confirm and Process Modal */}
      {processModal.isOpen && (
        <ConfirmationModal
          type={EConfirmationModalType.WARNING}
          isOpen
          title={`You are about to process this receipt. ${currencySymbol}${formatPriceCents(
            totalBookingsAllocatedCents
          )} will be allocated to the selected bookings.`}
          message={
            depositAllocation.amountCents !== 0 ? (
              <span>
                The remaining{' '}
                <span className="font-bold">
                  {currencySymbol}
                  {formatPriceCents(depositAllocation.amountCents)}
                </span>{' '}
                will be credited to the {selectedCompany?.name || ''}{' '}
                <span className="font-bold">Deposit Account.</span>
              </span>
            ) : (
              ''
            )
          }
          extraMessage={'Do you want to proceed?'}
          confirmButtonLabel="Yes"
          cancelButtonLabel="No"
          onConfirm={handleProcessConfirm}
          onCancel={() => processModal.handleClose()}
          isConfirmLoading={isProcessing}
        />
      )}
    </div>
  );
};
